import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { defaultBrandPresets, getAllBrandPresets } from '../config/brandPresets';
import { brandPresetStorage } from '../services/brandPresetStorage';
import toast from 'react-hot-toast';

const { FiPlus, FiEdit2, FiArchive, FiX, FiSave, FiAlertCircle, FiCheck, FiPackage } = FiIcons;

function BrandPresetManager({ isOpen, onClose, onPresetCreated }) {
  const [allPresets, setAllPresets] = useState({});
  const [archivedPresets, setArchivedPresets] = useState([]);
  const [editingPreset, setEditingPreset] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [showArchived, setShowArchived] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    domain: '',
    primaryColor: '#2563eb',
    secondaryColor: '#1d4ed8',
    backgroundColor: '#f8fafc',
    textColor: '#ffffff',
    logoUrl: '',
    layout: {
      borderRadius: 16,
      showHeader: true,
      headerStyle: 'solid',
      shadow: 'shadow-lg'
    }
  });

  useEffect(() => {
    loadPresets();
  }, []);

  const loadPresets = async () => {
    const presets = await getAllBrandPresets();
    const archived = await brandPresetStorage.getArchivedPresets();
    setAllPresets(presets);
    setArchivedPresets(archived);
  };

  const handleCreateNew = () => {
    setEditingPreset(null);
    setFormData({
      name: '',
      domain: '',
      primaryColor: '#2563eb',
      secondaryColor: '#1d4ed8',
      backgroundColor: '#f8fafc',
      textColor: '#ffffff',
      logoUrl: '',
      layout: {
        borderRadius: 16,
        showHeader: true,
        headerStyle: 'solid',
        shadow: 'shadow-lg'
      }
    });
    setShowForm(true);
  };

  const handleEdit = (preset) => {
    setEditingPreset(preset);
    setFormData(preset);
    setShowForm(true);
  };

  const handleArchive = async (presetId) => {
    if (window.confirm('Are you sure you want to archive this brand preset?')) {
      try {
        await brandPresetStorage.archivePreset(presetId);
        await loadPresets();
        toast.success('Brand preset archived successfully');
        if (onPresetCreated) onPresetCreated();
      } catch (error) {
        toast.error('Failed to archive brand preset');
      }
    }
  };

  const handleUnarchive = async (presetId) => {
    try {
      await brandPresetStorage.unarchivePreset(presetId);
      await loadPresets();
      toast.success('Brand preset restored successfully');
      if (onPresetCreated) onPresetCreated();
    } catch (error) {
      toast.error('Failed to restore brand preset');
    }
  };

  const handleInputChange = (path, value) => {
    setFormData(prev => {
      const keys = path.split('.');
      const newData = { ...prev };
      let current = newData;
      
      for (let i = 0; i < keys.length - 1; i++) {
        current[keys[i]] = { ...current[keys[i]] };
        current = current[keys[i]];
      }
      
      current[keys[keys.length - 1]] = value;
      return newData;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const presetData = {
        ...formData,
        id: editingPreset ? editingPreset.id : formData.name.toLowerCase().replace(/[^a-z0-9]+/g, '-')
      };

      if (editingPreset) {
        await brandPresetStorage.updatePreset(editingPreset.id, presetData);
        toast.success('Brand preset updated successfully');
      } else {
        await brandPresetStorage.createPreset(presetData);
        toast.success('Brand preset created successfully');
      }

      await loadPresets();
      setShowForm(false);
      setEditingPreset(null);
      if (onPresetCreated) onPresetCreated();
    } catch (error) {
      toast.error('Failed to save brand preset');
    }
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.95, y: 20 }}
          animate={{ scale: 1, y: 0 }}
          exit={{ scale: 0.95, y: 20 }}
          className="bg-white rounded-3xl shadow-2xl max-w-6xl w-full max-h-[90vh] overflow-hidden"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="bg-gradient-to-r from-purple-600 to-pink-600 text-white p-6">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-2xl font-bold mb-1">Brand Preset Manager</h2>
                <p className="text-purple-100 text-sm">Create and manage custom brand wrappers</p>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setShowArchived(!showArchived)}
                  className={`px-4 py-2 rounded-xl font-semibold flex items-center gap-2 transition-colors ${
                    showArchived 
                      ? 'bg-white text-purple-600' 
                      : 'bg-white/20 text-white hover:bg-white/30'
                  }`}
                >
                  <SafeIcon icon={FiPackage} />
                  Archived ({archivedPresets.length})
                </button>
                {!showArchived && (
                  <button
                    onClick={handleCreateNew}
                    className="bg-white text-purple-600 px-4 py-2 rounded-xl font-semibold flex items-center gap-2 hover:bg-purple-50 transition-colors"
                  >
                    <SafeIcon icon={FiPlus} />
                    New Preset
                  </button>
                )}
                <button
                  onClick={onClose}
                  className="p-2 hover:bg-white/20 rounded-lg transition-colors"
                >
                  <SafeIcon icon={FiX} className="text-xl" />
                </button>
              </div>
            </div>
          </div>

          <div className="p-6 overflow-y-auto max-h-[calc(90vh-120px)]">
            {showForm ? (
              <motion.form
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                onSubmit={handleSubmit}
                className="space-y-6"
              >
                <div className="bg-blue-50 border-l-4 border-blue-500 p-4 rounded-r-xl mb-6">
                  <div className="flex items-start gap-3">
                    <SafeIcon icon={FiAlertCircle} className="text-blue-600 text-xl mt-0.5" />
                    <div>
                      <h3 className="font-semibold text-blue-900 mb-1">
                        {editingPreset ? 'Edit Brand Preset' : 'Create New Brand Preset'}
                      </h3>
                      <p className="text-sm text-blue-700">
                        Configure colors, logos, and styling options for your custom brand wrapper
                      </p>
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Brand Name *
                      </label>
                      <input
                        type="text"
                        value={formData.name}
                        onChange={(e) => handleInputChange('name', e.target.value)}
                        className="w-full px-4 py-2 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-purple-500 transition-all outline-none"
                        placeholder="e.g. My Custom Brand"
                        required
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Brand Domain
                      </label>
                      <input
                        type="url"
                        value={formData.domain || ''}
                        onChange={(e) => handleInputChange('domain', e.target.value)}
                        className="w-full px-4 py-2 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-purple-500 transition-all outline-none"
                        placeholder="https://tools.example.org"
                      />
                      <p className="text-xs text-gray-500 mt-1">Embed URLs will use this domain. Leave empty to use current site domain.</p>
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Logo URL
                      </label>
                      <input
                        type="url"
                        value={formData.logoUrl}
                        onChange={(e) => handleInputChange('logoUrl', e.target.value)}
                        className="w-full px-4 py-2 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-purple-500 transition-all outline-none"
                        placeholder="https://example.com/logo.png"
                      />
                      <p className="text-xs text-gray-500 mt-1">Leave empty to display brand name instead</p>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                          Primary Color *
                        </label>
                        <div className="flex gap-2">
                          <input
                            type="color"
                            value={formData.primaryColor}
                            onChange={(e) => handleInputChange('primaryColor', e.target.value)}
                            className="w-12 h-10 rounded-lg border border-gray-200 cursor-pointer"
                          />
                          <input
                            type="text"
                            value={formData.primaryColor}
                            onChange={(e) => handleInputChange('primaryColor', e.target.value)}
                            className="flex-1 px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg font-mono text-sm"
                          />
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                          Secondary Color *
                        </label>
                        <div className="flex gap-2">
                          <input
                            type="color"
                            value={formData.secondaryColor}
                            onChange={(e) => handleInputChange('secondaryColor', e.target.value)}
                            className="w-12 h-10 rounded-lg border border-gray-200 cursor-pointer"
                          />
                          <input
                            type="text"
                            value={formData.secondaryColor}
                            onChange={(e) => handleInputChange('secondaryColor', e.target.value)}
                            className="flex-1 px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg font-mono text-sm"
                          />
                        </div>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                          Background Color *
                        </label>
                        <div className="flex gap-2">
                          <input
                            type="color"
                            value={formData.backgroundColor}
                            onChange={(e) => handleInputChange('backgroundColor', e.target.value)}
                            className="w-12 h-10 rounded-lg border border-gray-200 cursor-pointer"
                          />
                          <input
                            type="text"
                            value={formData.backgroundColor}
                            onChange={(e) => handleInputChange('backgroundColor', e.target.value)}
                            className="flex-1 px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg font-mono text-sm"
                          />
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                          Text Color *
                        </label>
                        <div className="flex gap-2">
                          <input
                            type="color"
                            value={formData.textColor}
                            onChange={(e) => handleInputChange('textColor', e.target.value)}
                            className="w-12 h-10 rounded-lg border border-gray-200 cursor-pointer"
                          />
                          <input
                            type="text"
                            value={formData.textColor}
                            onChange={(e) => handleInputChange('textColor', e.target.value)}
                            className="flex-1 px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg font-mono text-sm"
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Header Style *
                      </label>
                      <div className="grid grid-cols-3 gap-2">
                        {['solid', 'gradient', 'minimal'].map(style => (
                          <button
                            key={style}
                            type="button"
                            onClick={() => handleInputChange('layout.headerStyle', style)}
                            className={`py-2 px-3 rounded-lg text-xs font-medium border-2 transition-all capitalize ${
                              formData.layout.headerStyle === style
                                ? 'border-purple-500 bg-purple-50 text-purple-700'
                                : 'border-gray-200 text-gray-600 hover:border-gray-300'
                            }`}
                          >
                            {style}
                          </button>
                        ))}
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Border Radius (px) *
                      </label>
                      <input
                        type="number"
                        value={formData.layout.borderRadius}
                        onChange={(e) => handleInputChange('layout.borderRadius', parseInt(e.target.value))}
                        className="w-full px-4 py-2 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-purple-500 transition-all outline-none"
                        min="0"
                        max="50"
                      />
                      <div className="mt-2 h-2 bg-gray-200 rounded-full">
                        <div
                          className="h-full bg-purple-500 rounded-full transition-all"
                          style={{ width: `${(formData.layout.borderRadius / 50) * 100}%` }}
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Shadow Style *
                      </label>
                      <select
                        value={formData.layout.shadow}
                        onChange={(e) => handleInputChange('layout.shadow', e.target.value)}
                        className="w-full px-4 py-2 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-purple-500 transition-all outline-none"
                      >
                        <option value="shadow-none">None</option>
                        <option value="shadow-sm">Small</option>
                        <option value="shadow-md">Medium</option>
                        <option value="shadow-lg">Large</option>
                        <option value="shadow-xl">Extra Large</option>
                        <option value="shadow-2xl">2X Large</option>
                      </select>
                    </div>

                    <div className="flex items-center justify-between p-4 bg-gray-50 rounded-xl">
                      <label className="text-sm font-semibold text-gray-700">Show Header by Default</label>
                      <button
                        type="button"
                        onClick={() => handleInputChange('layout.showHeader', !formData.layout.showHeader)}
                        className={`relative w-14 h-7 rounded-full transition-colors ${
                          formData.layout.showHeader ? 'bg-purple-500' : 'bg-gray-300'
                        }`}
                      >
                        <motion.div
                          animate={{ x: formData.layout.showHeader ? 28 : 2 }}
                          transition={{ type: 'spring', stiffness: 500, damping: 30 }}
                          className="absolute top-1 w-5 h-5 bg-white rounded-full shadow-md"
                        />
                      </button>
                    </div>

                    <div className="border-2 border-gray-200 rounded-xl p-4">
                      <p className="text-xs font-semibold text-gray-700 mb-3">Live Preview</p>
                      <div
                        className="h-24 rounded-xl flex items-center justify-center"
                        style={{
                          background: formData.layout.headerStyle === 'gradient'
                            ? `linear-gradient(to right, ${formData.primaryColor}, ${formData.secondaryColor})`
                            : formData.layout.headerStyle === 'minimal'
                              ? formData.backgroundColor
                              : formData.primaryColor,
                          borderRadius: `${formData.layout.borderRadius}px`
                        }}
                      >
                        {formData.logoUrl ? (
                          <img src={formData.logoUrl} alt="Preview" className="h-12 w-auto object-contain" />
                        ) : (
                          <div
                            className="text-lg font-bold"
                            style={{ color: formData.layout.headerStyle === 'minimal' ? '#374151' : formData.textColor }}
                          >
                            {formData.name || 'Brand Name'}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="flex gap-4 pt-4 border-t">
                  <button
                    type="submit"
                    className="flex-1 bg-purple-600 text-white font-bold py-3 rounded-xl shadow-lg hover:bg-purple-700 transition-all flex items-center justify-center gap-2"
                  >
                    <SafeIcon icon={FiSave} />
                    {editingPreset ? 'Update Preset' : 'Create Preset'}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setShowForm(false);
                      setEditingPreset(null);
                    }}
                    className="px-6 py-3 bg-gray-100 text-gray-600 font-bold rounded-xl hover:bg-gray-200 transition-all"
                  >
                    Cancel
                  </button>
                </div>
              </motion.form>
            ) : showArchived ? (
              <div className="space-y-4">
                <div className="bg-gray-50 border-l-4 border-gray-400 p-4 rounded-r-xl mb-6">
                  <h3 className="font-semibold text-gray-900 mb-1">Archived Brand Presets</h3>
                  <p className="text-sm text-gray-600">
                    These presets are archived and won't appear in the brand selector. You can restore them anytime.
                  </p>
                </div>

                {archivedPresets.length === 0 ? (
                  <div className="text-center py-12">
                    <SafeIcon icon={FiPackage} className="text-4xl text-gray-400 mb-4 mx-auto" />
                    <h3 className="text-lg font-medium text-gray-900 mb-2">No archived presets</h3>
                    <p className="text-gray-500">Archived brand presets will appear here</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {archivedPresets.map((preset) => (
                      <motion.div
                        key={preset.id}
                        initial={{ opacity: 0, scale: 0.95 }}
                        animate={{ opacity: 1, scale: 1 }}
                        className="bg-white border-2 border-gray-200 rounded-2xl overflow-hidden opacity-75"
                      >
                        <div
                          className="h-24 flex items-center justify-center relative"
                          style={{
                            background: preset.layout.headerStyle === 'gradient'
                              ? `linear-gradient(to right, ${preset.primaryColor}, ${preset.secondaryColor})`
                              : preset.layout.headerStyle === 'minimal'
                                ? preset.backgroundColor
                                : preset.primaryColor
                          }}
                        >
                          <div className="absolute top-2 right-2 bg-gray-900/80 text-white px-2 py-1 rounded-lg text-xs font-bold">
                            Archived
                          </div>
                          {preset.logoUrl ? (
                            <img src={preset.logoUrl} alt={preset.name} className="h-12 w-auto object-contain" />
                          ) : (
                            <div
                              className="text-xl font-bold"
                              style={{ color: preset.textColor }}
                            >
                              {preset.name}
                            </div>
                          )}
                        </div>

                        <div className="p-5">
                          <h3 className="font-bold text-gray-900 mb-3 text-lg">{preset.name}</h3>
                          <button
                            onClick={() => handleUnarchive(preset.id)}
                            className="w-full py-2 rounded-lg text-sm font-medium transition-colors bg-blue-100 text-blue-700 hover:bg-blue-200 flex items-center justify-center gap-1"
                          >
                            <SafeIcon icon={FiCheck} className="text-xs" />
                            Restore
                          </button>
                        </div>
                      </motion.div>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {Object.entries(allPresets).map(([key, preset]) => (
                  <motion.div
                    key={key}
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    whileHover={{ y: -5 }}
                    className="bg-white border-2 border-gray-200 rounded-2xl overflow-hidden hover:border-purple-400 hover:shadow-xl transition-all"
                  >
                    <div
                      className="h-24 flex items-center justify-center relative"
                      style={{
                        background: preset.layout.headerStyle === 'gradient'
                          ? `linear-gradient(to right, ${preset.primaryColor}, ${preset.secondaryColor})`
                          : preset.layout.headerStyle === 'minimal'
                            ? preset.backgroundColor
                            : preset.primaryColor
                      }}
                    >
                      {preset.isDefault && (
                        <div className="absolute top-2 right-2 bg-white/90 text-purple-700 px-2 py-1 rounded-lg text-xs font-bold">
                          Built-in
                        </div>
                      )}
                      {preset.logoUrl ? (
                        <img src={preset.logoUrl} alt={preset.name} className="h-12 w-auto object-contain" />
                      ) : (
                        <div
                          className="text-xl font-bold"
                          style={{ color: preset.textColor }}
                        >
                          {preset.name}
                        </div>
                      )}
                    </div>

                    <div className="p-5">
                      <h3 className="font-bold text-gray-900 mb-3 text-lg">{preset.name}</h3>
                      <div className="space-y-2 text-sm mb-4">
                        <div className="flex items-center justify-between">
                          <span className="text-gray-600">Style:</span>
                          <span className="font-medium capitalize">{preset.layout.headerStyle}</span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-gray-600">Radius:</span>
                          <span className="font-medium">{preset.layout.borderRadius}px</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-gray-600">Colors:</span>
                          <div className="flex gap-1">
                            <div
                              className="w-5 h-5 rounded-full border-2 border-white shadow"
                              style={{ backgroundColor: preset.primaryColor }}
                            />
                            <div
                              className="w-5 h-5 rounded-full border-2 border-white shadow"
                              style={{ backgroundColor: preset.secondaryColor }}
                            />
                          </div>
                        </div>
                        {preset.domain && (
                          <div className="flex items-center justify-between">
                            <span className="text-gray-600">Domain:</span>
                            <span className="font-medium text-xs truncate max-w-[150px]">{preset.domain.replace(/^https?:\/\//, '')}</span>
                          </div>
                        )}
                      </div>

                      <div className="flex gap-2">
                        <button
                          onClick={() => handleEdit(preset)}
                          className="flex-1 py-2 rounded-lg text-sm font-medium transition-colors bg-purple-100 text-purple-700 hover:bg-purple-200 flex items-center justify-center gap-1"
                        >
                          <SafeIcon icon={FiEdit2} className="text-xs" />
                          Edit
                        </button>
                        <button
                          onClick={() => handleArchive(preset.id)}
                          className="flex-1 py-2 rounded-lg text-sm font-medium transition-colors bg-orange-100 text-orange-700 hover:bg-orange-200 flex items-center justify-center gap-1"
                        >
                          <SafeIcon icon={FiArchive} className="text-xs" />
                          Archive
                        </button>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            )}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}

export default BrandPresetManager;